import { Controller } from "@hotwired/stimulus"

// Controller for managing multi-organization donations on Giving Days
// Uses the checkout_donations API for persistence
export default class extends Controller {
  static targets = [
    "searchInput",
    "searchResults",
    "selectedOrganizations",
    "addButton",
    "hiddenField",
    "totalDisplay",
    "amountInput"
  ]

  static values = {
    cartId: String,
    givingDayId: String,
    primaryOrgId: String
  }

  connect() {
    console.log("Organization Selector Controller connected", {
      cartId: this.cartIdValue,
      givingDayId: this.givingDayIdValue
    })
    this.selectedOrgs = new Map()
    this.searchTimeout = null

    // Load existing donations from the API
    this.loadExistingDonations()

    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside.bind(this))
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
    document.removeEventListener('click', this.handleClickOutside.bind(this))
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideSearchResults()
    }
  }

  async loadExistingDonations() {
    try {
      const response = await fetch(this.apiUrl('/api/v1/checkout_donations'))
      const data = await response.json()

      if (data.donations) {
        // Clear existing display
        this.selectedOrganizationsTarget.innerHTML = ''

        data.donations.forEach(donation => {
          // Skip the primary organization (handled by the main donation form)
          const isPrimaryOrg = this.primaryOrgIdValue &&
            donation.organization_id.toString() === this.primaryOrgIdValue.toString()

          if (donation.organization_id && !donation.legacy && !isPrimaryOrg) {
            const org = {
              id: donation.id, // Use the cart item ID for updates/deletes
              organizationId: donation.organization_id,
              name: donation.organization_name,
              amount: parseFloat(donation.amount) || 0
            }
            this.selectedOrgs.set(donation.organization_id.toString(), org)
            this.renderOrganizationItem(org)
          }
        })

        this.updateTotal()
      }
    } catch (error) {
      console.error("Error loading existing donations:", error)
    }
  }

  search(event) {
    clearTimeout(this.searchTimeout)
    const query = event.target.value.trim()

    // Debounce API search
    this.searchTimeout = setTimeout(() => {
      this.searchOrganizations(query)
    }, 200)
  }

  async searchOrganizations(query) {
    try {
      let url = this.apiUrl('/api/v1/checkout_donations/organizations?per_page=10')
      if (query.length > 0) {
        url = this.apiUrl(`/api/v1/checkout_donations/organizations?q=${encodeURIComponent(query)}&per_page=10`)
      }

      const response = await fetch(url)
      const data = await response.json()

      this.displaySearchResults(data.organizations || [])
    } catch (error) {
      console.error("Error searching organizations:", error)
    }
  }

  showInitialResults() {
    // Show first 10 organizations when focusing
    const query = this.searchInputTarget.value.trim()
    this.searchOrganizations(query)
  }

  displaySearchResults(organizations) {
    // Filter out already selected organizations and the primary org (main donation on page)
    const available = organizations.filter(org => {
      const orgId = org.id.toString()
      // Exclude if already in additional donations
      if (this.selectedOrgs.has(orgId)) return false
      // Exclude primary organization (the one from the URL/main donation)
      if (this.primaryOrgIdValue && orgId === this.primaryOrgIdValue.toString()) return false
      return true
    })

    if (available.length === 0) {
      this.searchResultsTarget.innerHTML = `
        <div class="list-group-item text-muted small">
          No organizations found
        </div>
      `
      this.showSearchResults()
      return
    }

    const resultsHTML = available.map(org => `
      <button type="button"
              class="list-group-item list-group-item-action d-flex align-items-center py-2"
              data-action="click->organization-selector#selectOrganization"
              data-org-id="${org.id}"
              data-org-name="${this.escapeHtml(org.name)}"
              data-org-logo="${org.logo_url || ''}">
        ${org.logo_url ? `<img src="${org.logo_url}" alt="" class="me-2 rounded" style="width: 32px; height: 32px; object-fit: contain;">` : '<div class="me-2 rounded bg-light d-flex align-items-center justify-content-center" style="width: 32px; height: 32px;"><i class="fas fa-building text-muted small"></i></div>'}
        <span class="small">${this.escapeHtml(org.name)}</span>
      </button>
    `).join('')

    this.searchResultsTarget.innerHTML = resultsHTML
    this.showSearchResults()
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  async selectOrganization(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const orgId = button.dataset.orgId
    const orgName = button.dataset.orgName
    const defaultAmount = 25.00

    this.hideSearchResults()
    this.searchInputTarget.value = ''

    // Add via API
    try {
      const response = await fetch(this.apiUrl('/api/v1/checkout_donations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organization_id: orgId,
          amount: defaultAmount
        })
      })

      const data = await response.json()

      if (data.donation) {
        const org = {
          id: data.donation.id, // Cart item ID
          organizationId: data.donation.organization_id,
          name: data.donation.organization_name,
          amount: parseFloat(data.donation.amount) || defaultAmount
        }
        this.selectedOrgs.set(orgId.toString(), org)
        this.renderOrganizationItem(org)
        this.updateTotal()

        // Update the Purchase Summary
        this.addDonationToSummary(org)
        this.updateSummaryTotals(data.cart_total)

        // Dispatch event for cart totals
        this.dispatch("donationAdded", { detail: { donation: data.donation, cartTotal: data.cart_total } })
      }
    } catch (error) {
      console.error("Error adding organization:", error)
    }
  }

  renderOrganizationItem(org) {
    const orgHTML = `
      <div class="org-donation-item d-flex align-items-center gap-3 p-3 mb-2 bg-white rounded border" data-organization-id="${org.organizationId}" data-item-id="${org.id}">
        <div class="org-info flex-grow-1">
          <div class="org-name fw-medium">${this.escapeHtml(org.name)}</div>
        </div>

        <div class="org-amount" style="width: 100px;">
          <div class="input-group input-group-sm">
            <span class="input-group-text">$</span>
            <input class="form-control"
                   type="number"
                   step="1"
                   min="1"
                   value="${org.amount}"
                   placeholder="Amount"
                   data-organization-selector-target="amountInput"
                   data-action="change->organization-selector#updateAmount"
                   data-org-id="${org.organizationId}"
                   data-item-id="${org.id}">
          </div>
        </div>

        <button class="btn btn-link text-muted p-0"
                type="button"
                data-action="click->organization-selector#removeOrganization"
                data-organization-id="${org.organizationId}"
                data-item-id="${org.id}"
                title="Remove">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `

    this.selectedOrganizationsTarget.insertAdjacentHTML('beforeend', orgHTML)
  }

  async removeOrganization(event) {
    const button = event.currentTarget
    const orgId = button.dataset.organizationId
    const itemId = button.dataset.itemId

    try {
      const response = await fetch(this.apiUrl(`/api/v1/checkout_donations/${itemId}`), {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        this.selectedOrgs.delete(orgId)
        const item = this.selectedOrganizationsTarget.querySelector(`[data-organization-id="${orgId}"]`)
        if (item) {
          item.remove()
        }
        this.updateTotal()

        // Update the Purchase Summary
        this.removeDonationFromSummary(itemId)
        this.updateSummaryTotals(data.cart_total)

        // Dispatch event for cart totals
        this.dispatch("donationRemoved", { detail: { cartTotal: data.cart_total } })
      }
    } catch (error) {
      console.error("Error removing organization:", error)
    }
  }

  async updateAmount(event) {
    const input = event.target
    const orgId = input.dataset.orgId
    const itemId = input.dataset.itemId
    const amount = parseFloat(input.value) || 0

    if (amount <= 0) {
      input.value = 1
      return
    }

    try {
      const response = await fetch(this.apiUrl(`/api/v1/checkout_donations/${itemId}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: amount })
      })

      const data = await response.json()

      if (data.donation) {
        const org = this.selectedOrgs.get(orgId)
        if (org) {
          org.amount = parseFloat(data.donation.amount)
          this.selectedOrgs.set(orgId, org)
        }
        this.updateTotal()

        // Update the Purchase Summary
        this.updateDonationInSummary(itemId, data.donation.amount)
        this.updateSummaryTotals(data.cart_total)

        // Dispatch event for cart totals
        this.dispatch("donationUpdated", { detail: { donation: data.donation, cartTotal: data.cart_total } })
      }
    } catch (error) {
      console.error("Error updating amount:", error)
    }
  }

  updateTotal() {
    const total = Array.from(this.selectedOrgs.values())
      .reduce((sum, org) => sum + org.amount, 0)

    if (this.hasTotalDisplayTarget) {
      this.totalDisplayTarget.textContent = this.formatCurrency(total)
    }

    // Dispatch event for cart totals controller
    this.dispatch("totalChanged", { detail: { total } })
  }

  showSearchResults() {
    this.searchResultsTarget.classList.remove('d-none')
  }

  hideSearchResults() {
    this.searchResultsTarget.classList.add('d-none')
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  // Build API URL with cart_id parameter
  apiUrl(path) {
    const url = new URL(path, window.location.origin)
    if (this.cartIdValue) {
      url.searchParams.set('cart_id', this.cartIdValue)
    }
    if (this.givingDayIdValue) {
      url.searchParams.set('giving_day_id', this.givingDayIdValue)
    }
    return url.toString()
  }

  // Purchase Summary update methods
  addDonationToSummary(org) {
    const donationsSection = document.querySelector('[data-cart-totals-target="donationsSection"]')
    if (!donationsSection) return

    const donationHTML = `
      <div class="donation-row d-flex justify-content-between align-items-start mb-2" data-donation-item-id="${org.id}">
        <div class="donation-info">
          <div class="donation-org">${this.escapeHtml(org.name)}</div>
        </div>
        <div class="donation-amount">${this.formatCurrency(org.amount)}</div>
      </div>
    `
    donationsSection.insertAdjacentHTML('beforeend', donationHTML)

    // Update section header to "Donations" if more than one
    const header = donationsSection.closest('.summary-section')?.querySelector('h6')
    if (header) {
      header.textContent = 'Donations'
    }
  }

  removeDonationFromSummary(itemId) {
    const row = document.querySelector(`[data-donation-item-id="${itemId}"]`)
    if (row) {
      row.remove()
    }
  }

  updateDonationInSummary(itemId, amount) {
    const row = document.querySelector(`[data-donation-item-id="${itemId}"]`)
    if (row) {
      const amountEl = row.querySelector('.donation-amount')
      if (amountEl) {
        amountEl.textContent = this.formatCurrency(parseFloat(amount))
      }
    }
  }

  updateSummaryTotals(cartTotal) {
    // Calculate total donations from all donations in the summary
    let donationsTotal = 0
    document.querySelectorAll('[data-cart-totals-target="donationsSection"] .donation-amount').forEach(el => {
      const amount = parseFloat(el.textContent.replace(/[^0-9.-]+/g, '')) || 0
      donationsTotal += amount
    })

    // Update donations subtotal
    const donationsSubtotal = document.querySelector('[data-cart-totals-target="donationsSubtotal"]')
    if (donationsSubtotal) {
      donationsSubtotal.textContent = this.formatCurrency(donationsTotal)
    }

    // Calculate and update fee coverage (5% + $0.30, grossed up)
    // This matches the server-side calculation in BusinessRules::Fees
    const percentage = 0.022 + 0.028 // GIVSUM_STRIPE_NONPROFIT_PROCESSING + PLATFORM_FEE = 5%
    const perChargeFee = 0.30 // PAYMENT_PROCESSOR_TRANSACTION_FEE
    let fee = (donationsTotal * percentage) + perChargeFee
    fee = fee / (1 - percentage) // Gross up for fee coverage
    fee = Math.round(fee * 100) / 100 // Round to 2 decimal places

    const feeCoverage = document.querySelector('[data-cart-totals-target="feeCoverage"]')
    if (feeCoverage) {
      feeCoverage.textContent = this.formatCurrency(fee)
    }

    // Update grand total (donations + fee)
    const grandTotal = document.querySelector('[data-cart-totals-target="grandTotal"]')
    if (grandTotal) {
      const total = donationsTotal + fee
      grandTotal.textContent = this.formatCurrency(total)
    }

    // Update the fee checkbox text if it exists
    this.updateFeeCoverageCheckboxText(donationsTotal, fee)
  }

  updateFeeCoverageCheckboxText(donationsTotal, fee) {
    // Find the fee coverage checkbox label and update the amounts
    const feeLabel = document.querySelector('label[for*="fee_coverage"]')
    if (feeLabel) {
      const primaryOrgName = document.querySelector('.donation-org')?.textContent || 'the organization'
      feeLabel.innerHTML = `I wish to donate an extra ${this.formatCurrency(fee)} so that the organizations receive ${this.formatCurrency(donationsTotal)} after fees`
    }
  }
}
